import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { extractSpotPrice } from '../../../lib/snapshot-search';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';

export const evaluateIronCondor: StrategyEvaluator = (marketData: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult[] | null => {
    if (!marketData) return null;

    const { activeRange, gap } = context;
    const spot = extractSpotPrice(marketData) || 0;
    if (spot <= 0) return null;
    
    const atmStrike = Math.round(spot / gap) * gap;

    // Inner legs (Sell), Outer legs (Buy)
    // We use a fraction of activeRange to ensure they are spaced out
    const innerDist = Math.max(2, Math.floor(activeRange / 3));
    const outerDist = Math.max(4, innerDist + Math.floor(activeRange / 3));

    const sellCeStrike = atmStrike + (innerDist * gap);
    const buyCeStrike = atmStrike + (outerDist * gap);
    
    const sellPeStrike = atmStrike - (innerDist * gap);
    const buyPeStrike = atmStrike - (outerDist * gap);

    const liveRawOptsArr = context.chain || marketData.options || marketData.optionChain || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);

    const sellCeRow = liveFlatOpts.find((r: any) => r.strike === sellCeStrike && r.type === 'CE');
    const buyCeRow = liveFlatOpts.find((r: any) => r.strike === buyCeStrike && r.type === 'CE');
    const sellPeRow = liveFlatOpts.find((r: any) => r.strike === sellPeStrike && r.type === 'PE');
    const buyPeRow = liveFlatOpts.find((r: any) => r.strike === buyPeStrike && r.type === 'PE');

    const signals: SignalResult[] = [];

    if (sellCeRow) {
        const entryPrice = sellCeRow.ltp || sellCeRow.close || sellCeRow.price || 0;
        if (entryPrice > 0) signals.push({ trade_type: 'BEAR', strike: sellCeStrike, option_type: 'CE', entry_price: entryPrice, remarks: `Iron Condor: Sell OTM CE (${sellCeStrike})`, exact_match: true });
    }

    if (buyCeRow) {
        const entryPrice = buyCeRow.ltp || buyCeRow.close || buyCeRow.price || 0;
        if (entryPrice > 0) signals.push({ trade_type: 'BULL', strike: buyCeStrike, option_type: 'CE', entry_price: entryPrice, remarks: `Iron Condor: Buy Far OTM CE (${buyCeStrike})`, exact_match: true });
    }

    if (sellPeRow) {
        const entryPrice = sellPeRow.ltp || sellPeRow.close || sellPeRow.price || 0;
        if (entryPrice > 0) signals.push({ trade_type: 'BULL', strike: sellPeStrike, option_type: 'PE', entry_price: entryPrice, remarks: `Iron Condor: Sell OTM PE (${sellPeStrike})`, exact_match: true });
    }

    if (buyPeRow) {
        const entryPrice = buyPeRow.ltp || buyPeRow.close || buyPeRow.price || 0;
        if (entryPrice > 0) signals.push({ trade_type: 'BEAR', strike: buyPeStrike, option_type: 'PE', entry_price: entryPrice, remarks: `Iron Condor: Buy Far OTM PE (${buyPeStrike})`, exact_match: true });
    }

    // Only return if we have all 4 legs to form a complete Iron Condor
    if (signals.length === 4) {
        return signals;
    }

    return null;
};
